import type { Theme } from '@mui/material/styles';
import type { SxProps } from '@mui/system';

import mixinSx from './mixinSx';
import { sharedShadow } from './styleUtils';

const baseTooltipStyles = {
  borderRadius: '10px',
  boxShadow: sharedShadow,
  zIndex: 2000,
  padding: '6px 8px',
  maxWidth: '320px',
  fontSize: '13px',
  fontWeight: 600,
};

const baseArrowStyles = {
  '& .MuiTooltip-arrow': {
    display: 'none',
  },
};

const darkVariantStyles = {
  backgroundColor: (theme: Theme) => theme.palette.grey100,
  color: (theme: Theme) => theme.palette.common.white,
};

const lightVariantStyles = {
  backgroundColor: (theme: Theme) => theme.palette.white,
  color: (theme: Theme) => theme.palette.grey70,
};

const getDarkOutline = () => (theme: Theme) =>
  `1px solid ${theme.palette.grey10}16`;

const getLightOutline = () => (theme: Theme) =>
  `1px solid ${theme.palette.grey100}16`;

// ========================================
// Legacy API Functions (for older MUI versions)
// ========================================

function createBaseTooltipStyles(additionalSx?: SxProps<Theme>) {
  const defaultSx = {
    '& .MuiTooltip-tooltip': {
      ...baseTooltipStyles,
      typography: (theme: Theme) => theme.typography.caption,
    },
    ...baseArrowStyles,
  };

  return {
    popper: {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, -6],
          },
        },
      ],
      sx: additionalSx ? mixinSx(defaultSx, additionalSx) : defaultSx,
    },
  };
}

export function tooltipDarkStyleProps(additionalSx?: SxProps<Theme>) {
  const darkStyles = {
    '& .MuiTooltip-tooltip': {
      ...darkVariantStyles,
      outline: getDarkOutline(),
    },
  };

  return createBaseTooltipStyles(
    additionalSx ? mixinSx(darkStyles, additionalSx) : darkStyles
  );
}

export function tooltipLightStyleProps(additionalSx?: SxProps<Theme>) {
  const lightStyles = {
    '& .MuiTooltip-tooltip': {
      ...lightVariantStyles,
      outline: getLightOutline(),
    },
  };

  return createBaseTooltipStyles(
    additionalSx ? mixinSx(lightStyles, additionalSx) : lightStyles
  );
}

/**
 * Tooltip Utilities Overview:
 *
 * We provide separate implementations for each MUI API to avoid type conflicts:
 *
 * **Legacy API (older MUI versions):**
 * - `tooltipDarkStyleProps()` and `tooltipLightStyleProps()`
 * - Uses `popper.sx` structure
 * - Uses `typography: (theme) => theme.typography.caption`
 *
 * **SlotProps API (MUI v5.14+):**
 * - `tooltipDarkSlotProps()` and `tooltipLightSlotProps()`
 * - Uses `slotProps.popper` structure
 * - Uses explicit CSS properties like `fontSize`, `fontWeight`
 * - Better TypeScript support and forward compatibility
 *
 * Usage: `tooltipProps={tooltipDarkSlotProps()}` on ui-core components
 */

// ========================================
// SlotProps API Functions (for MUI v5.14+)
// ========================================

/**
 * Dark tooltip styling for MUI v5.14+ using slotProps API
 * Returns props compatible with tooltipProps on ui-core components
 */
export function tooltipDarkSlotProps(additionalSx?: SxProps<Theme>) {
  const baseStyles = {
    '& .MuiTooltip-tooltip': {
      ...baseTooltipStyles,
      ...darkVariantStyles,
      outline: getDarkOutline(),
    },
    ...baseArrowStyles,
  };

  return {
    slotProps: {
      popper: {
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -8],
            },
          },
        ],
        sx: additionalSx ? mixinSx(baseStyles, additionalSx) : baseStyles,
      },
    },
  };
}

/**
 * Light tooltip styling for MUI v5.14+ using slotProps API
 * Returns props compatible with tooltipProps on ui-core components
 */
export function tooltipLightSlotProps(additionalSx?: SxProps<Theme>) {
  const baseStyles = {
    '& .MuiTooltip-tooltip': {
      ...baseTooltipStyles,
      ...lightVariantStyles,
      outline: getLightOutline(),
    },
    ...baseArrowStyles,
  };

  return {
    slotProps: {
      popper: {
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -8],
            },
          },
        ],
        sx: additionalSx ? mixinSx(baseStyles, additionalSx) : baseStyles,
      },
    },
  };
}
